'use client';

import { useEffect } from 'react';

/**
 * Révèle au scroll les éléments `.rv` (classe `.in` ajoutée par IntersectionObserver).
 * - Mouvement : `html.motion` est posé par le script inline de app/layout.tsx, sauf si
 *   prefers-reduced-motion est actif ; sans lui tout est affiché d'emblée (CSS).
 * - Un élément qui reçoit le focus clavier est révélé immédiatement (focusin).
 * - Ne jamais l'utiliser sur le titre du hero (LCP).
 * `deps` : relance l'observation quand le contenu change (ex. changement de page).
 */
export function useReveal(deps: unknown[] = []) {
	useEffect(() => {
		const root = document.documentElement;
		const els = Array.from(document.querySelectorAll<HTMLElement>('.rv:not(.in)'));
		if (!root.classList.contains('motion') || !('IntersectionObserver' in window)) {
			els.forEach((el) => el.classList.add('in'));
			return;
		}
		const io = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('in');
						io.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
		);
		els.forEach((el) => io.observe(el));
		const onFocus = (e: FocusEvent) => {
			const target = e.target as Element | null;
			const rv = target?.closest?.('.rv');
			if (rv) rv.classList.add('in');
		};
		document.addEventListener('focusin', onFocus);
		return () => {
			io.disconnect();
			document.removeEventListener('focusin', onFocus);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);
}
